import React, { useState, useEffect } from 'react';
import { useSeller } from '../../context/SellerContext';
import { useShopping } from '../../context/ShoppingContext';
import { auth } from '../../utils/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { validateMobileNumber, validateOTP } from '../../utils/validation';
import { sellerAuthAPI, handleAPIError } from '../../utils/sellerAPI';
import '../../styles/SellerRegistration.css';

const MobileVerification = ({ onNext }) => {
  const {
    formData,
    updateFormData,
    errors,
    setError,
    clearAllErrors,
    loading,
    setLoading,
    otpSent,
    setOtpSent,
    verificationStep,
    setVerificationStep,
    setAuthData,
  } = useSeller();

  const { user } = useShopping(); // Get user from main authentication context

  const [timer, setTimer] = useState(0);
  const [confirmationResult, setConfirmationResult] = useState(null);

  // Auto-login if user is already authenticated
  useEffect(() => {
    if (user && user.phoneNumber) {
      // Extract phone number without country code
      const phoneNumber = user.phoneNumber.replace(/^\+91/, '').replace(/\s/g, '');
      updateFormData('mobileNumber', phoneNumber);

      // Since the user is already authenticated via main app, we can skip OTP verification
      // and directly progress to business details
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        onNext();
      }, 1500); // Show welcome message for 1.5 seconds
    }
  }, [user]);

  // Timer for OTP resend
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        'size': 'invisible',
        'callback': (response) => {
          // reCAPTCHA solved, allow signInWithPhoneNumber.
        },
        'expired-callback': () => {
          // Response expired. Ask user to solve reCAPTCHA again.
          setError('mobileNumber', 'Recaptcha expired, please try again');
        }
      });
    }
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    updateFormData('mobileNumber', value);
    clearAllErrors('mobileNumber');
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const error = validateMobileNumber(formData.mobileNumber);

    if (error) {
      setError('mobileNumber', error);
      return;
    }

    setLoading(true);
    try {
      setupRecaptcha();
      const appVerifier = window.recaptchaVerifier;
      const phoneNumber = `+91${formData.mobileNumber}`;

      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(confirmation);

      setOtpSent(true);
      setVerificationStep('otp');
      setTimer(60); // 60 seconds timer
    } catch (err) {
      console.error("Error sending OTP:", err);
      let message = err.message;
      if (err.code === 'auth/invalid-phone-number') {
        message = 'Invalid phone number format.';
      } else if (err.code === 'auth/too-many-requests') {
        message = 'Too many requests. Please try again later.';
      }
      setError('mobileNumber', message);
      // Reset recaptcha
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (value) => {
    updateFormData('otp', value);
    clearAllErrors('otp');
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const error = validateOTP(formData.otp);

    if (error) {
      setError('otp', error);
      return;
    }

    if (!confirmationResult) {
      setError('otp', 'Session expired. Please resend OTP.');
      return;
    }

    setLoading(true);
    try {
      // 1. Verify OTP with Firebase
      const result = await confirmationResult.confirm(formData.otp);
      const user = result.user;
      const idToken = await user.getIdToken();

      // 2. Verify with Backend and Create/Login Seller
      const response = await sellerAuthAPI.verifyFirebaseToken(
        formData.mobileNumber,
        idToken
      );

      if (response.status === 200) {
        const sellerDetails =
          response.data.seller || { mobileNumber: formData.mobileNumber };
        setAuthData(response.data.token, sellerDetails);
        setVerificationStep('business');
        onNext();
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      let message = err.message;
      if (err.code === 'auth/invalid-verification-code') {
        message = 'Invalid OTP. Please check and try again.';
      } else if (err.response) {
        const apiError = handleAPIError(err);
        message = apiError.message;
      }
      setError('otp', message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      // Clear previous recaptcha
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }

      setupRecaptcha();
      const appVerifier = window.recaptchaVerifier;
      const phoneNumber = `+91${formData.mobileNumber}`;

      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      setConfirmationResult(confirmation);
      setTimer(60);
    } catch (err) {
      console.error("Error resending OTP:", err);
      setError('mobileNumber', err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading screen when auto-logging in existing user
  if (user && user.phoneNumber && loading) {
    return (
      <div className="mobile-verify-container">
        <div className="mobile-verify-left">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h2 className="mobile-verify-title">Welcome back!</h2>
            <p className="mobile-verify-subtitle" style={{ marginBottom: '32px' }}>
              We're setting up your seller account using your existing profile...
            </p>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔄</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-verify-container">
      {/* Left Section - Form */}
      <div className="mobile-verify-left">
        <div style={{ marginBottom: '32px' }}>
          <h2 className="mobile-verify-title">
            {user ? `Welcome back, ${user.displayName || 'User'}!` : 'Welcome to Kalinga Crafts Store'}
          </h2>
          <p className="mobile-verify-subtitle">
            {user
              ? 'Complete your seller profile to start selling'
              : 'Create your account to start selling'
            }
          </p>
          {user && user.email && !user.phoneNumber && (
            <p style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
              📧 Using account: {user.email}
            </p>
          )}
        </div>

        <form onSubmit={verificationStep === 'mobile' ? handleSendOTP : handleVerifyOTP} className="mobile-verify-form">

          {/* Recaptcha Container */}
          <div id="recaptcha-container"></div>

          {/* Mobile Number Section */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
              Enter Mobile Number
            </label>
            <div className="mobile-input-group">
              <div className="mobile-input-wrapper">
                <span className="mobile-prefix">
                  +91 -
                </span>
                <input
                  type="tel"
                  value={formData.mobileNumber}
                  onChange={handleMobileChange}
                  disabled={verificationStep === 'otp'}
                  placeholder="Mobile Number"
                  maxLength="10"
                  className="mobile-input-field"
                  style={{
                    backgroundColor: verificationStep === 'otp' ? '#f5f5f5' : '#fff'
                  }}
                />
              </div>
              {verificationStep === 'mobile' && (
                <button
                  onClick={handleSendOTP}
                  disabled={loading || !formData.mobileNumber || formData.mobileNumber.length < 10}
                  className="send-otp-btn"
                >
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              )}
            </div>
            {errors.mobileNumber && (
              <p style={{ color: '#e63946', fontSize: '12px', marginTop: '4px' }}>{errors.mobileNumber}</p>
            )}
          </div>

          {/* OTP Section */}
          <div>
            <input
              type="text"
              placeholder="Enter OTP"
              disabled={verificationStep === 'mobile'}
              value={formData.otp}
              onChange={(e) => handleOTPChange(e.target.value)}
              maxLength="6"
              className={`otp-input-field ${errors.otp ? 'error' : ''}`}
              style={{
                backgroundColor: verificationStep === 'mobile' ? '#f5f5f5' : '#fff',
              }}
            />
            {errors.otp && (
              <p style={{ color: '#e63946', fontSize: '12px', marginTop: '4px' }}>{errors.otp}</p>
            )}
            {verificationStep === 'otp' && (
              <div className="otp-timer-container">
                {timer > 0 ? (
                  <span style={{ color: '#666' }}>Resend OTP in {timer}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    className="resend-otp-btn"
                  >
                    Resend OTP
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setVerificationStep('mobile');
                    setOtpSent(false);
                    updateFormData('otp', '');
                    clearAllErrors();
                    if (window.recaptchaVerifier) {
                      window.recaptchaVerifier.clear();
                      window.recaptchaVerifier = null;
                    }
                  }}
                  className="change-number-btn"
                >
                  Change Number
                </button>
              </div>
            )}
          </div>

          {/* WhatsApp Checkbox */}
          <div className="whatsapp-checkbox">
            <input
              type="checkbox"
              defaultChecked
            />
            <span style={{ fontSize: '14px', color: '#333' }}>
              I want to receive important updates on <span style={{ fontWeight: '600', color: '#25D366' }}>WhatsApp</span>
            </span>
          </div>

          {/* Create Account Button */}
          <button
            type="submit"
            disabled={verificationStep === 'mobile' || loading}
            className="create-account-btn"
          >
            {loading && verificationStep === 'otp' ? 'Verifying...' : 'Create Account'}
          </button>

          <p className="terms-text">
            By clicking you agree to our <span className="terms-link">Terms & Conditions</span> and <span className="terms-link">Privacy Policy</span>
          </p>

        </form>
      </div>

      {/* Right Section - Benefits */}
      <div className="mobile-verify-right">
        <h3 className="benefits-title">
          Grow your business faster by selling on Kalinga Crafts Store
        </h3>

        <div className="benefits-grid">
          <div className="benefit-stat">
            <div className="benefit-stat-icon">👥</div>
            <div>
              <p className="benefit-stat-value">11 lakh+</p>
              <p className="benefit-stat-label">Suppliers are selling commission-free</p>
            </div>
          </div>
          <div className="benefit-stat">
            <div className="benefit-stat-icon">📍</div>
            <div>
              <p className="benefit-stat-value">19000+</p>
              <p className="benefit-stat-label">Pincodes supported for delivery</p>
            </div>
          </div>
          <div className="benefit-stat">
            <div className="benefit-stat-icon">👨‍👩‍👧‍👦</div>
            <div>
              <p className="benefit-stat-value">Crore of</p>
              <p className="benefit-stat-label">Customers buy across India</p>
            </div>
          </div>
          <div className="benefit-stat">
            <div className="benefit-stat-icon">🏷️</div>
            <div>
              <p className="benefit-stat-value">700 +</p>
              <p className="benefit-stat-label">Categories to sell</p>
            </div>
          </div>
        </div>

        <div className="requirements-section">
          <h4 className="requirements-title">
            All you need to sell on Kalinga Crafts Store is:
          </h4>

          <div className="requirements-list">
            <div className="requirement-item">
              <span className="check-icon">✓</span>
              <div className="requirement-text">
                <p className="requirement-label">Tax Details</p>
                <p className="requirement-sub">
                  Enrolment ID/UIN <span className="non-gst-badge">For Non-GST sellers</span>
                </p>
                <p className="requirement-sub">GSTIN (Regular & Composition GST sellers)</p>
              </div>
            </div>

            <div className="requirement-item">
              <span className="check-icon">✓</span>
              <div className="requirement-text">
                <p className="requirement-label">Bank Account</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileVerification;
